/**
 * OnboardingTenantPage — cadastro público de novo tenant lava-jato.
 * Acessivel em https://gestão.octawash.com.br/onboarding (sem login).
 */
import { traduzirErroSupabase } from '@octawash/shared';
import { useMutation } from '@tanstack/react-query';
import { Building2, Check } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

interface ResultadoOk {
  cod_tenant: string;
  admin_email: string;
  proximos_passos: string[];
}

export function OnboardingTenantPage() {
  const [form, setForm] = useState({
    nom_tenant: '',
    num_cnpj: '',
    des_subdominio: '',
    cod_cor_primaria: '#0EA5E9',
    admin_nome: '',
    admin_email: '',
    admin_senha: '',
    admin_whatsapp: '',
    admin_cpf: '',
    ind_aceite_termos: false,
  });
  const [resultado, setResultado] = useState<ResultadoOk | null>(null);

  const criar = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('criar-tenant-self-serve', {
        body: form,
      });
      if (error) throw error;
      if ((data as { erros?: string[] }).erros) {
        throw new Error((data as { erros: string[] }).erros.join(', '));
      }
      return data as ResultadoOk;
    },
    onSuccess: (r) => {
      setResultado(r);
      toast.success('Tenant criado com sucesso!');
    },
    onError: (e: Error) => toast.error('Falha', { description: traduzirErroSupabase(e) }),
  });

  if (resultado) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
              <Check className="h-7 w-7 text-emerald-600" />
            </div>
            <CardTitle>Tudo pronto!</CardTitle>
            <p className="text-muted-foreground text-sm">
              Seu lava-jato <strong>{resultado.cod_tenant}</strong> esta cadastrado no Octa Pontos.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted rounded p-4">
              <p className="mb-2 text-sm font-semibold">Próximos passos:</p>
              <ol className="list-inside list-decimal space-y-1 text-sm">
                {resultado.proximos_passos.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ol>
            </div>
            <p className="text-muted-foreground text-center text-xs">
              E-mail de confirmacao enviado para <strong>{resultado.admin_email}</strong>
            </p>
            <Button asChild className="w-full">
              <a href="/login">Ir para o login</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="bg-primary/10 mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full">
            <Building2 className="text-primary h-7 w-7" />
          </div>
          <CardTitle>Cadastre seu lava-jato no Octa Pontos</CardTitle>
          <p className="text-muted-foreground text-sm">
            Plataforma multi-tenant. Sem cartao, sem fidelidade.
          </p>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              criar.mutate();
            }}
            className="space-y-6"
          >
            {/* Empresa */}
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold">1. Sua empresa</legend>
              <div className="space-y-1">
                <Label htmlFor="nom_tenant">Nome do lava-jato</Label>
                <Input
                  id="nom_tenant"
                  value={form.nom_tenant}
                  onChange={(e) => setForm({ ...form, nom_tenant: e.target.value })}
                  placeholder="Ex: Lava ACME"
                  required
                  minLength={3}
                />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="num_cnpj">CNPJ</Label>
                  <Input
                    id="num_cnpj"
                    value={form.num_cnpj}
                    onChange={(e) => setForm({ ...form, num_cnpj: e.target.value })}
                    placeholder="00.000.000/0000-00"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="des_subdominio">Subdominio</Label>
                  <Input
                    id="des_subdominio"
                    value={form.des_subdominio}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        des_subdominio: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                      })
                    }
                    placeholder="acme"
                    required
                    pattern="[a-z0-9][a-z0-9-]{1,30}[a-z0-9]"
                  />
                  <p className="text-muted-foreground text-xs">
                    {form.des_subdominio || 'subdominio'}.
                    {typeof window !== 'undefined'
                      ? window.location.host.replace(/^gestao\./, '')
                      : 'wash.octasistemas.com'}
                  </p>
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="cor">Cor primaria da marca</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="cor"
                    value={form.cod_cor_primaria}
                    onChange={(e) => setForm({ ...form, cod_cor_primaria: e.target.value })}
                    className="h-10 w-16 cursor-pointer rounded border"
                  />
                  <Input
                    value={form.cod_cor_primaria}
                    onChange={(e) => setForm({ ...form, cod_cor_primaria: e.target.value })}
                    className="tracking-wider"
                    placeholder="#0EA5E9"
                  />
                </div>
              </div>
            </fieldset>

            {/* Admin */}
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold">2. Voce (admin/DPO)</legend>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="admin_nome">Nome completo</Label>
                  <Input
                    id="admin_nome"
                    value={form.admin_nome}
                    onChange={(e) => setForm({ ...form, admin_nome: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="admin_cpf">CPF</Label>
                  <Input
                    id="admin_cpf"
                    value={form.admin_cpf}
                    onChange={(e) => setForm({ ...form, admin_cpf: e.target.value })}
                    placeholder="000.000.000-00"
                    required
                  />
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="admin_email">E-mail</Label>
                  <Input
                    id="admin_email"
                    type="email"
                    value={form.admin_email}
                    onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="admin_whatsapp">WhatsApp</Label>
                  <Input
                    id="admin_whatsapp"
                    value={form.admin_whatsapp}
                    onChange={(e) => setForm({ ...form, admin_whatsapp: e.target.value })}
                    placeholder="+55 11 99999-9999"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="admin_senha">Senha (min 10 chars)</Label>
                <Input
                  id="admin_senha"
                  type="password"
                  value={form.admin_senha}
                  onChange={(e) => setForm({ ...form, admin_senha: e.target.value })}
                  required
                  minLength={10}
                  autoComplete="new-password"
                />
              </div>
            </fieldset>

            {/* Termos */}
            <fieldset className="space-y-2">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.ind_aceite_termos}
                  onChange={(e) => setForm({ ...form, ind_aceite_termos: e.target.checked })}
                  required
                  className="mt-1"
                />
                <span>
                  Li e aceito os{' '}
                  <a href="/termos" target="_blank" className="text-primary hover:underline">
                    Termos de Uso
                  </a>{' '}
                  e a{' '}
                  <a href="/privacidade" target="_blank" className="text-primary hover:underline">
                    Politica de Privacidade
                  </a>
                  . Confirmo que sou autorizado a representar a empresa.
                </span>
              </label>
            </fieldset>

            <Button
              type="submit"
              disabled={criar.isPending || !form.ind_aceite_termos}
              className="w-full"
              size="lg"
            >
              {criar.isPending ? 'Criando...' : 'Cadastrar meu lava-jato'}
            </Button>

            <p className="text-muted-foreground text-center text-xs">
              Ja tem conta?{' '}
              <a href="/login" className="text-primary hover:underline">
                Entrar
              </a>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
